import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Sparkles, Zap, Brain } from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo } from '../../data/mock';

const HeroSection = () => {
  return (
    <section className="relative min-h-[calc(100vh-80px)] lg:min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-white pb-0">
      {/* Background Elements */}
      <div className="absolute inset-0 ai-grid-pattern" />
      
      {/* Floating AI Elements */}
      <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
      
      {/* AI Circuit Lines */}
      <div className="absolute top-20 right-10 opacity-20">
        <svg width="200" height="200" viewBox="0 0 200 200" fill="none">
          <path d="M10 100 H90 V50 H150" stroke="#2563eb" strokeWidth="2" strokeDasharray="5,5" />
          <circle cx="150" cy="50" r="8" fill="#2563eb" />
          <path d="M10 150 H60 V100 H180" stroke="#2563eb" strokeWidth="2" strokeDasharray="5,5" />
          <circle cx="180" cy="100" r="8" fill="#2563eb" />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="text-left">
            {/* AI Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-8 animate-fade-in">
              <Sparkles className="w-4 h-4 text-blue-600 mr-2" />
              <span className="text-sm font-medium text-blue-700">AI-Powered Solutions • 10+ Years Experience</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 animate-slide-up">
              <span className="text-gray-900">AI-Powered </span>
              <span className="text-gradient-blue">Promotion & PR</span>
              <br />
              <span className="text-gray-900">to Accelerate Your </span>
              <span className="text-blue-600">Growth</span>
            </h1>

            {/* Subtext */}
            <p className="text-lg text-gray-600 max-w-xl mb-10 leading-relaxed animate-slide-up-delay">
              {companyInfo.subtext}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4 animate-slide-up-delay-2">
              <Link to="/quote">
                <Button
                  size="lg"
                  className="bg-blue-600 text-white hover:bg-blue-700 px-8 py-6 text-base font-semibold shadow-lg shadow-blue-600/25 group"
                >
                  Get a Free Quote
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <a href={companyInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 px-8 py-6 text-base font-semibold"
                >
                  <MessageCircle className="mr-2 w-4 h-4" />
                  Chat on WhatsApp
                </Button>
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center gap-6 mt-10 animate-slide-up-delay-2">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                    {['PD', 'AI', 'PR', 'MK'][i - 1]}
                  </div>
                ))}
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">250+</span> businesses trust us
              </div>
            </div>
          </div>

          {/* Right Content - AI Visual */}
          <div className="relative hidden lg:block animate-slide-in-right">
            <div className="relative">
              {/* Main AI Visual Card */}
              <div className="relative bg-white rounded-3xl p-8 shadow-2xl shadow-blue-500/10 border border-gray-100">
                {/* AI Brain Illustration */}
                <div className="relative h-80 flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white rounded-2xl" />
                  <div className="relative">
                    <div className="w-40 h-40 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center animate-pulse-glow">
                      <Brain className="w-20 h-20 text-white" />
                    </div>
                    {/* Orbiting elements */}
                    <div className="absolute -top-4 -right-4 w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center animate-float">
                      <Zap className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="absolute -bottom-4 -left-4 w-14 h-14 bg-white rounded-xl shadow-lg flex items-center justify-center animate-float" style={{ animationDelay: '0.5s' }}>
                      <Sparkles className="w-7 h-7 text-blue-600" />
                    </div>
                  </div>
                </div>
                
                {/* Stats bar */}
                <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">98%</div>
                    <div className="text-xs text-gray-500">AI Accuracy</div>
                  </div>
                  <div className="text-center border-x border-gray-100">
                    <div className="text-2xl font-bold text-blue-600">3x</div>
                    <div className="text-xs text-gray-500">Faster Results</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">24/7</div>
                    <div className="text-xs text-gray-500">AI Support</div>
                  </div>
                </div>
              </div>

              {/* Floating cards */}
              <div className="absolute -top-6 -left-6 bg-white rounded-xl p-4 shadow-xl border border-gray-100 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Campaign Live</div>
                    <div className="text-xs text-gray-500">AI optimized</div>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-4 shadow-xl border border-gray-100 animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">+127%</div>
                    <div className="text-xs text-gray-500">Growth achieved</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default HeroSection;
